import { useInfiniteQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Page } from "components/page";
import { useLandlordSettingsProvider } from "contexts/landlord-settings-provider";
import { PropertySelector } from "modules/landlord-portal/components/property-selector";
import {
  landlordMaintenanceTask,
  type MaintenanceTaskStatusFilter,
} from "modules/landlord-portal/models/maintenance";
import { LandlordMaintenanceList } from "modules/landlord-portal/screens/maintenance/components/landlord-maintenance-list";

/**
 * Refreshes the page and its state when the ownership or the selected property
 * changes — both live in the query key, so React Query refetches from page 1
 * on its own and the accumulated pages reset.
 */
function useMaintenanceTasks(
  ownershipId: string,
  status: MaintenanceTaskStatusFilter,
  propertyId: string | null,
) {
  const { data, isLoading, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery(
      landlordMaintenanceTask.infiniteQueries.read(
        ownershipId,
        status,
        propertyId,
      ),
    );

  return {
    tasks: data?.pages.flatMap((page) => page.data) ?? [],
    pagination: data?.pages.at(-1)?.pagination,
    isLoading,
    isFetchingMore: isFetchingNextPage,
    showMore: () => void fetchNextPage(),
  };
}

export function MaintenanceScreen() {
  const { ownershipId } = useLandlordSettingsProvider();
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get("property_id");

  const active = useMaintenanceTasks(ownershipId, "active", propertyId);
  const past = useMaintenanceTasks(ownershipId, "past", propertyId);

  return (
    <Page heading={"Maintenance"} rightElement={<PropertySelector />}>
      <LandlordMaintenanceList
        isLoadingActive={active.isLoading}
        isLoadingPast={past.isLoading}
        isFetchingMoreActive={active.isFetchingMore}
        isFetchingMorePast={past.isFetchingMore}
        activeTasks={active.tasks}
        pastTasks={past.tasks}
        activePagination={active.pagination}
        pastPagination={past.pagination}
        onShowMoreActive={active.showMore}
        onShowMorePast={past.showMore}
      />
    </Page>
  );
}
